<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import AdminLayout from '@/layouts/AdminLayout.vue'
import UsuarioAdminForm, { type UsuarioAdminFormData } from '@/components/UsuarioAdminForm.vue'
import { createUsuario } from '@/api/usuarios'
import { listProjetos } from '@/api/projetos'
import { getApiErrorMessage } from '@/api/client'
import { useAuth } from '@/composables/useAuth'
import type { UsuarioTipo } from '@/types/api'
import type { Projeto } from '@/types/projetos'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const router = useRouter()
const { usuario: usuarioLogado } = useAuth()

const saving = ref(false)
const projetos = ref<Projeto[]>([])

const tiposDisponiveis = computed<UsuarioTipo[]>(() => {
  if (usuarioLogado.value?.tipo === 'Master') {
    return ['Master', 'Admin']
  }

  return ['Admin']
})

const form = ref<UsuarioAdminFormData>({
  nome: '',
  login: '',
  senha: '',
  tipo: 'Admin',
  ativo: true,
  projeto_ids: [],
})

async function onSubmit(): Promise<void> {
  const nome = form.value.nome.trim()
  const login = form.value.login.trim()
  const senha = form.value.senha

  if (nome === '' || login === '' || senha === '') {
    await Swal.fire({
      icon: 'warning',
      title: 'Campos obrigatórios',
      text: 'Informe nome, login e senha.',
    })
    return
  }

  if (!tiposDisponiveis.value.includes(form.value.tipo)) {
    await Swal.fire({
      icon: 'warning',
      title: 'Tipo inválido',
      text: 'Selecione um tipo permitido para o seu perfil.',
    })
    return
  }

  saving.value = true

  try {
    const usuario = await createUsuario({
      nome,
      login,
      senha,
      tipo: form.value.tipo,
      ativo: form.value.ativo,
      ...(form.value.tipo === 'Admin' ? { projeto_ids: form.value.projeto_ids } : {}),
    })

    await router.replace(`/admin/usuarios/${usuario.id}`)
    await Swal.fire({
      icon: 'success',
      title: 'Usuário criado',
      timer: 1500,
      showConfirmButton: false,
    })
  } catch (err) {
    await Swal.fire({
      icon: 'error',
      title: 'Não foi possível criar',
      text: getApiErrorMessage(err, 'Erro ao criar usuário'),
    })
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  try {
    projetos.value = await listProjetos()
  } catch {
    projetos.value = []
  }
})
</script>

<template>
  <AdminLayout>
    <template #title>
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div>
          <h1 class="h5 mb-1">Novo usuário</h1>
          <p class="text-muted small mb-0">Defina nome, login, senha, tipo e situação.</p>
        </div>
        <RouterLink class="btn btn-outline-secondary btn-sm" to="/admin/usuarios">
          Voltar
        </RouterLink>
      </div>
    </template>

    <div class="card border-0 shadow-sm">
      <div class="card-body p-4">
        <UsuarioAdminForm
          v-model="form"
          :tipos-disponiveis="tiposDisponiveis"
          :projetos="projetos"
          require-senha
          :saving="saving"
          @submit="onSubmit"
        >
          <template #actions>
            <button
              type="submit"
              class="btn btn-dark"
              :disabled="saving"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              {{ saving ? 'Salvando…' : 'Criar usuário' }}
            </button>
          </template>
        </UsuarioAdminForm>
      </div>
    </div>
  </AdminLayout>
</template>
